<template>
    <div class="bg-white rounded-lg shadow overflow-hidden">
      <div class="p-6">
        <h2 class="text-xl font-semibold mb-6">Invoice History</h2>
  
        <!-- Invoices Table -->
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-white uppercase">Date</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-white uppercase">Invoice #</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-white uppercase">Amount</th>
                <th class="px-6 py-3 text-right text-xs font-medium text-white uppercase">Actions</th>
              </tr>
            </thead>
            <tbody class="bg-gray-50 divide-y divide-gray-200">
              <tr v-for="inv in invoices" :key="inv.id" class="hover:bg-gray-50">
                <td class="px-6 py-4 whitespace-nowrap text-sm ">
                  {{ formatDate(inv.created) }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm">
                  {{ inv.number || inv.id }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm">
                  {{ formatCurrency(inv.amount_due ?? inv.total, inv.currency) }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                    <div class="flex space-x-5 justify-end">
                        <button
                    @click="downloadInvoicePdf(inv.id)"
                    class="text-blue-600 hover:text-blue-900"
                    :disabled="isDownloading === inv.id"
                    aria-label="Download PDF"
                    title="Download PDF"
                  >
                  <inline-svg name="download" classes="w-4 h-4 text-blue-600 hover:text-blue-900"></inline-svg>

                  </button>
                  <a
                    v-if="inv.hosted_invoice_url"
                    :href="inv.hosted_invoice_url"
                    target="_blank"
                    rel="noopener"
                    class="text-blue-600 hover:text-blue-900"
                    aria-label="Open hosted invoice"
                    title="Open hosted invoice"
                  >
                  <inline-svg name="external-link-alt" classes="w-4 h-4 text-blue-600 hover:text-blue-900"></inline-svg>
                  </a>
                    </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
  
        <!-- Load More -->
        <div v-if="hasMore" class="mt-4 text-center">
          <button @click="loadMore" :disabled="isLoading" class="btn btn-outline">
            {{ isLoading ? 'Loading...' : 'Load More' }}
          </button>
        </div>
  
        <p v-if="invoices.length === 0 && !isLoading" class="text-gray-500 text-center py-4">
          No invoices found.
        </p>
      </div>
    </div>
  </template>
  
  <script>
  export default {
    name: 'InvoiceHistory',
  
    data() {
      return {
        isLoading: false,
        isDownloading: null,
        invoices: [],
        hasMore: false,
        lastInvoiceId: null,
      };
    },
  
    async mounted() {
      await this.fetchInvoices();
    },
  
    methods: {
      // Safely unwrap axios/plain responses & support multiple shapes
      unwrap(res) {
        // axios({data:{success:true,data:[...]}}) -> [...]
        // axios({data:{items:[...]}}) -> [...]
        // axios({data:[...]}) -> [...]
        // direct -> [...]
        const root = res?.data ?? res ?? null;
        if (!root) return null;
        // Common shapes:
        if (Array.isArray(root)) return root;
        if (Array.isArray(root.data)) return root.data;
        if (Array.isArray(root.items)) return root.items;
        return root;
      },
  
      getHasMore(res) {
        const root = res?.data ?? res ?? {};
        // Support both has_more and hasMore
        return Boolean(root.has_more ?? root.hasMore ?? false);
      },
  
      getLastId(list) {
        if (!list || list.length === 0) return null;
        return list[list.length - 1].id ?? null;
      },
  
      async fetchInvoices() {
        if (this.isLoading) return;
        this.isLoading = true;
        try {
          // Stripe API uses 'starting_after' (snake_case)
          const params = { limit: 10, starting_after: this.lastInvoiceId };
          const res = await this.$store.dispatch('billing/fetchInvoices', params);
  
          const list = this.unwrap(res) || [];
          // If backend returned the entire envelope, attempt to pull list out
          const items = Array.isArray(list) ? list : (list?.data ?? list?.items ?? []);
  
          if (!Array.isArray(items)) {
            // Not an array? Treat as error-like for UX
            throw new Error('Unexpected invoices response shape.');
          }
  
          this.invoices = [...this.invoices, ...items];
          this.hasMore = this.getHasMore(res);
          this.lastInvoiceId = this.getLastId(this.invoices);
        } catch (error) {
          console.error('fetchInvoices error:', error);
          this.$toast?.error?.('Failed to load invoices');
        } finally {
          this.isLoading = false;
        }
      },
  
      async loadMore() {
        await this.fetchInvoices();
      },
  
      formatDate(unixSeconds) {
        const date = new Date((Number(unixSeconds) || 0) * 1000);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
      },
  
      formatCurrency(amount, currency = 'USD') {
        const cents = Number(amount || 0);
        const code = String(currency || 'USD').toUpperCase(); // Intl expects UPPERCASE
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: code,
        }).format(cents / 100);
      },
  
      async downloadInvoicePdf(invoiceId) {
        this.isDownloading = invoiceId;
        try {
          await this.$store.dispatch('billing/downloadInvoicePdf', invoiceId);
          // If your action returns a blob/URL, you can handle it here instead.
        } catch (error) {
          console.error('downloadInvoicePdf error:', error);
          this.$toast?.error?.('Failed to download invoice');
        } finally {
          this.isDownloading = null;
        }
      },
    },
  };
  </script>
  